import type { Heat } from '../../types/heats';

/**
 * Modèle du planning : une journée est une SUITE, pas une hiérarchie.
 *
 * Le regroupement d'origine était fait par identité (`date → wod → catégorie`,
 * en `Map`) : toutes les heats d'un même WOD tombaient dans la même case quel
 * que soit leur horaire. Un WOD ne pouvait donc apparaître qu'une fois par jour,
 * et « WOD 3 à 10h, WOD 4 à 11h, WOD 3 à 12h » était structurellement
 * impossible à représenter.
 *
 * Ici, on parcourt la journée dans l'ordre des heures et on ouvre un nouveau
 * bloc dès que le couple (WOD, catégorie) change. Les en-têtes ne sont donc pas
 * des contenants : ce sont des ÉTIQUETTES sur des suites contiguës, qui se
 * scindent et se reforment toutes seules. C'est ce qui rend « ça recrée les
 * parents » gratuit — il n'y a rien à créer.
 */

/** Clé de catégorie d'une heat : un groupe de catégories prime sur la catégorie. */
export const catKeyOf = (h: Heat): string =>
  (h.category_group_id || h.category_id || 'no-category') as string;

export const wodKeyOf = (h: Heat): string => (h.wod_id || 'no-wod') as string;

export const byStartTime = (a: Heat, b: Heat) =>
  (a.start_time || '').localeCompare(b.start_time || '');

/** Une ligne de la liste plate : en-tête de WOD, en-tête de catégorie, ou heat. */
export interface PlanRow {
  kind: 'wod' | 'category' | 'heat';
  /** Identifiant stable pour React et pour dnd. Unique dans la journée. */
  key: string;
  /**
   * Heats que cette ligne emporte quand on la déplace. Une heat n'emporte
   * qu'elle-même ; un en-tête emporte tout SON bloc — pas toutes les heats du
   * WOD de la journée, car le même WOD peut avoir plusieurs blocs.
   */
  heatIds: string[];
  /** Index, dans la suite des heats du jour, de la première heat de la ligne. */
  firstHeatIndex: number;
  label: string;
  /** Renseigné sur les lignes `heat`. */
  heat?: Heat;
  /** Nombre de heats du bloc, pour les en-têtes. */
  blockSize?: number;
  /** Vrai quand l'en-tête de catégorie porte un groupe de catégories. */
  isMultiCategory?: boolean;
  /** Identités portées par la ligne, pour les actions (déplacer vers un jour…). */
  wodId?: string;
  categoryId?: string;
}

export interface BuildRowsInput {
  /** Heats de LA journée affichée, dans n'importe quel ordre. */
  heats: Heat[];
  wodName: (wodId: string, sample: Heat) => string;
  categoryName: (catId: string, sample: Heat) => string;
}

/**
 * Construit la liste plate affichée pour une journée.
 *
 * Un en-tête de WOD s'ouvre quand le WOD change ; un en-tête de catégorie
 * s'ouvre quand le WOD **ou** la catégorie change. Les deux en-têtes
 * apparaissent donc ensemble au début d'un bloc de WOD, ce qui est voulu : on
 * lit « WOD 3 » puis « Team H/H », et chacun se saisit séparément.
 *
 * **Sauf quand aucune heat du jour ne porte d'épreuve.** L'en-tête est une
 * étiquette sur une suite ; si toutes les heats sont sans WOD, elle n'annonce
 * qu'une absence, la même pour tout le monde, au-dessus de chaque catégorie.
 * Sur un événement course dont la course n'a pas été associée aux vagues, cela
 * donnait un « Sans WOD » en tête de planning — du vocabulaire CrossFit pour
 * dire qu'il n'y a rien à dire (relevé par Bryan le 2026-08-17). Dès qu'UNE
 * heat porte une épreuve, l'en-tête redevient informatif et revient partout :
 * il distingue alors ce qui en a de ce qui n'en a pas.
 */
export function buildDayRows({ heats, wodName, categoryName }: BuildRowsInput): {
  rows: PlanRow[];
  /** Ordre chronologique des ids de heats du jour — la référence de position. */
  heatOrder: string[];
} {
  const ordered = [...heats].sort(byStartTime);
  const heatOrder = ordered.map(h => h.id);
  const rows: PlanRow[] = [];
  const aucuneEpreuve = ordered.every(h => !h.wod_id);

  let i = 0;
  let wodBlockIndex = 0;
  let catBlockIndex = 0;

  while (i < ordered.length) {
    const wodKey = wodKeyOf(ordered[i]);

    // Étendue du bloc de WOD : tant que le WOD ne change pas.
    let wodEnd = i;
    while (wodEnd < ordered.length && wodKeyOf(ordered[wodEnd]) === wodKey) wodEnd++;

    if (!aucuneEpreuve) {
      rows.push({
        kind: 'wod',
        key: `wod::${wodKey}::${wodBlockIndex}`,
        heatIds: ordered.slice(i, wodEnd).map(h => h.id),
        firstHeatIndex: i,
        label: wodName(wodKey, ordered[i]),
        blockSize: wodEnd - i,
        wodId: wodKey,
      });
    }
    wodBlockIndex++;

    // Blocs de catégorie À L'INTÉRIEUR de ce bloc de WOD.
    let j = i;
    while (j < wodEnd) {
      const catKey = catKeyOf(ordered[j]);
      let catEnd = j;
      while (catEnd < wodEnd && catKeyOf(ordered[catEnd]) === catKey) catEnd++;

      rows.push({
        kind: 'category',
        key: `category::${catKey}::${catBlockIndex}`,
        heatIds: ordered.slice(j, catEnd).map(h => h.id),
        firstHeatIndex: j,
        label: categoryName(catKey, ordered[j]),
        blockSize: catEnd - j,
        isMultiCategory: !!ordered[j].is_multi_category,
        wodId: wodKey,
        categoryId: catKey,
      });
      catBlockIndex++;

      for (let k = j; k < catEnd; k++) {
        rows.push({
          kind: 'heat',
          key: `heat::${ordered[k].id}`,
          heatIds: [ordered[k].id],
          firstHeatIndex: k,
          label: ordered[k].start_time || '',
          heat: ordered[k],
          wodId: wodKey,
          categoryId: catKey,
        });
      }

      j = catEnd;
    }

    i = wodEnd;
  }

  return { rows, heatOrder };
}

/**
 * Ligne devant laquelle tracer le trait d'insertion, pour un lâcher donné.
 *
 * Plusieurs lignes consécutives mènent au MÊME ordre final : en tête d'un bloc,
 * l'en-tête de WOD, celui de catégorie et la première heat désignent tous « juste
 * avant cette heat ». Trois traits possibles pour un seul résultat — l'écran
 * proposait un choix qui n'en était pas un.
 *
 * On remonte donc à la première ligne qui donne cette même position : la
 * frontière haute, celle qui se lit comme « ici, entre les deux blocs ».
 *
 * Ce n'est PAS une restriction : un WOD peut toujours se poser entre deux
 * catégories d'un autre WOD — et le scinder en deux. On cesse seulement
 * d'afficher trois fois la même destination.
 *
 * @returns la clé de la ligne à surmonter du trait, `'__end__'` pour la fin de
 *   journée, ou `null` s'il n'y a rien à montrer.
 */
/**
 * Ligne d'ancrage réelle d'un lâcher, dans l'espace d'index de dnd.
 *
 * ⚠️ Le point qui m'a fait faux : **dnd ne retire qu'UNE ligne** de sa liste,
 * celle qu'on a saisie. Les autres lignes du bloc déplacé (sous-en-têtes de
 * catégorie, heats du WOD) restent des `Draggable` à ses yeux et comptent donc
 * dans `destination.index`. Filtrer tout le bloc de son côté raccourcissait la
 * liste de ~10 lignes en déplaçant un WOD : l'ancrage partait alors très loin
 * de la souris.
 *
 * On travaille donc sur `rows` privée de la SEULE ligne source, puis on avance
 * jusqu'à la première ligne qui ne part pas avec le bloc — elle sera transparente
 * après le déplacement.
 */
function resolveAnchor(rows: PlanRow[], sourceIndex: number, destinationIndex: number) {
  const moved = rows[sourceIndex];
  const movedSet = new Set(moved.heatIds);
  const list = rows.filter((_, i) => i !== sourceIndex);

  let i = Math.max(0, destinationIndex);
  while (i < list.length && !list[i].heatIds.some(id => !movedSet.has(id))) i++;

  return { list, movedSet, index: i, row: i < list.length ? list[i] : null };
}


export function dropHintKey(
  rows: PlanRow[],
  sourceIndex: number,
  destinationIndex: number,
): string | null {
  if (!rows[sourceIndex]) return null;

  const { list, movedSet, index, row } = resolveAnchor(rows, sourceIndex, destinationIndex);
  if (!row) return '__end__';

  const target = row.heatIds.find(id => !movedSet.has(id));

  // Remonter tant que la ligne précédente vise la même heat : en tête d'un bloc,
  // en-tête de WOD, en-tête de catégorie et première heat désignent tous le même
  // endroit. Les lignes du bloc déplacé sont sautées, elles vont disparaître.
  let i = index;
  while (i > 0) {
    const prev = list[i - 1];
    const prevTarget = prev.heatIds.find(id => !movedSet.has(id));
    if (prevTarget !== target) break;
    i -= 1;
  }
  return list[i].key;
}


/**
 * Nouvel ordre des heats du jour après un lâcher, quelle que soit la GRANULARITÉ
 * de ce qui a été saisi.
 *
 * Un seul calcul pour les trois prises (heat, catégorie, WOD) : la ligne saisie
 * emporte ses heats, on les retire de la suite, et on les réinsère devant la
 * heat qui ouvre la ligne de destination. C'est ce qui rend vrai « tout
 * s'insère n'importe où » — il n'y a plus de contenant à respecter, seulement
 * une position dans le temps.
 *
 * L'identité des heats n'est JAMAIS touchée : déplacer change QUAND une heat
 * part, jamais À QUOI ni À QUI elle appartient. Une heat lâchée au milieu d'un
 * autre WOD coupe ce bloc en deux et porte son propre en-tête au rendu suivant.
 *
 * @param sourceIndex index de la ligne saisie, dans `rows`
 * @param destinationIndex index de dépôt, tel que le donne dnd — c'est-à-dire
 *   dans la liste PRIVÉE de la ligne saisie
 * @returns l'ordre complet des ids du jour, ou `null` si rien ne bouge
 */
export function reorderDayHeats(
  rows: PlanRow[],
  heatOrder: string[],
  sourceIndex: number,
  destinationIndex: number,
): string[] | null {
  const moved = rows[sourceIndex];
  if (!moved) return null;

  const movedSet = new Set(moved.heatIds);
  const remaining = heatOrder.filter(id => !movedSet.has(id));
  if (remaining.length === heatOrder.length) return null; // rien à déplacer

  // Même résolution d'ancrage que le trait d'insertion — sinon l'indicateur
  // montrerait une place et le lâcher en produirait une autre.
  const { movedSet: ms, row } = resolveAnchor(rows, sourceIndex, destinationIndex);
  const target = row?.heatIds.find(id => !ms.has(id));
  const insertAt = target !== undefined ? remaining.indexOf(target) : remaining.length;
  if (insertAt < 0) return null;

  const next = [
    ...remaining.slice(0, insertAt),
    ...moved.heatIds,
    ...remaining.slice(insertAt),
  ];

  // Un ordre identique ne vaut pas un aller-retour au serveur.
  return next.every((id, i) => id === heatOrder[i]) ? null : next;
}
